import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { localizedPath } from "../../hooks/useLocalizedNavigate";
import s from "./BanerCookies.module.css";

/**
 * BANER COOKIES — kafelek w prawym dolnym rogu, ponad widgetem Teams.
 *
 * [polecenie właściciela 2026-09-20] „dodaj baner cookies w takim stylu (…) tylko jako taki sam
 * kafelek w prawym dolnym rogu, z-index ponad widget teams”. Wygląd wg wzoru właściciela: biała
 * karta z zaokrągleniem 20px, ✖ w rogu, ikona ciasteczka, jedno zdanie, koralowy przycisk zgody.
 *
 * ZGODA jest zapamiętywana w przeglądarce (localStorage) — raz na urządzenie. ✖ chowa baner tylko
 * do końca sesji karty (sessionStorage): zamknięcie bez decyzji nie jest zgodą. Link prowadzi do
 * publicznej polityki cookies (/dokumenty/cookies) — tej samej, którą edytuje administrator.
 */
export const KLUCZ_ZGODY_COOKIES = "mp:cookies-zgoda";
export const KLUCZ_ODLOZENIA_COOKIES = "mp:cookies-odlozone";

const czytaj = (magazyn, klucz) => {
    try { return magazyn.getItem(klucz); } catch { return null; }
};
const zapisz = (magazyn, klucz, wartosc) => {
    try { magazyn.setItem(klucz, wartosc); } catch { /* prywatne okno / zablokowane dane */ }
};

/* [polecenie właściciela 2026-09-21] „zaakceptowany, to znika — ciasteczka zapisane, a teraz baner jest co logowanie”.
   Zgoda stała WYŁĄCZNIE w localStorage, a ✖ tylko w sessionStorage — więc każda nowa karta po ✖ i każda
   przeglądarka czyszcząca dane witryny (tryb firmowy Edge, „wyczyść przy zamknięciu”) pokazywała baner od nowa.
   Teraz decyzja ma DWA nośniki: localStorage i trwałe ciasteczko pierwszej strony. Wystarczy, że przeżyje jeden.
   Wylogowanie żadnego z nich nie dotyka (AuthContext.logout czyści tylko klucze sesji). */
export const CIASTECZKO_ZGODY = "mp_cookies_zgoda";
const ROK_S = 365 * 24 * 3600;
const DNI_ODLOZENIA = 30;

const czytajCiasteczko = (nazwa, dokument = typeof document !== "undefined" ? document : null) => {
    try {
        const m = (dokument?.cookie || "").split("; ").find(c => c.startsWith(nazwa + "="));
        return m ? decodeURIComponent(m.slice(nazwa.length + 1)) : null;
    } catch { return null; }
};
const zapiszCiasteczko = (nazwa, wartosc, sekundy) => {
    try {
        const bezpieczne = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `${nazwa}=${encodeURIComponent(wartosc)}; Max-Age=${sekundy}; Path=/; SameSite=Lax${bezpieczne}`;
    } catch { /* ciasteczka zablokowane */ }
};

/** Czy baner ma się pokazać — funkcja czysta względem magazynów (zamrażalna zapadką). */
export function czyPokazacBanerCookies(local = window.localStorage, session = window.sessionStorage, dokument = undefined, teraz = Date.now()) {
    if (czytaj(local, KLUCZ_ZGODY_COOKIES)) return false;
    const ciastko = czytajCiasteczko(CIASTECZKO_ZGODY, dokument === undefined ? (typeof document !== "undefined" ? document : null) : dokument);
    if (ciastko === "tak") return false;
    // ✖ = „nie teraz”: cisza na 30 dni, nie do końca karty. To nadal NIE jest zgoda — po terminie baner wraca.
    const odlozoneDo = Number(czytaj(local, KLUCZ_ODLOZENIA_COOKIES) || (ciastko && ciastko.startsWith("odl:") ? ciastko.slice(4) : 0));
    if (odlozoneDo && odlozoneDo > teraz) return false;
    if (czytaj(session, KLUCZ_ODLOZENIA_COOKIES)) return false;
    return true;
}

const IkonaCiastka = () => (
    <svg className={s.ikona} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <path
            d="M32 4c-1.9 0-3.7.2-5.5.6a7 7 0 0 0 5.1 11.6 7 7 0 0 0 6.9 8.4 7 7 0 0 0 8.5 6.9 7 7 0 0 0 12.4 3.3A28 28 0 1 1 32 4z"
            fill="#d9a066"
        />
        <circle cx="20" cy="26" r="3.2" fill="#6b3e26" />
        <circle cx="27" cy="42" r="3.6" fill="#6b3e26" />
        <circle cx="42" cy="44" r="3" fill="#6b3e26" />
        <circle cx="38" cy="31" r="2.4" fill="#6b3e26" />
        <circle cx="17" cy="40" r="2" fill="#6b3e26" />
    </svg>
);

const BanerCookies = () => {
    const [widoczny, setWidoczny] = useState(false);

    useEffect(() => {
        setWidoczny(czyPokazacBanerCookies());
    }, []);

    const zaakceptuj = useCallback(() => {
        zapisz(window.localStorage, KLUCZ_ZGODY_COOKIES, new Date().toISOString());
        zapiszCiasteczko(CIASTECZKO_ZGODY, "tak", ROK_S);
        setWidoczny(false);
    }, []);

    const odloz = useCallback(() => {
        const doKiedy = String(Date.now() + DNI_ODLOZENIA * 24 * 3600 * 1000);
        zapisz(window.sessionStorage, KLUCZ_ODLOZENIA_COOKIES, "1");
        zapisz(window.localStorage, KLUCZ_ODLOZENIA_COOKIES, doKiedy);
        zapiszCiasteczko(CIASTECZKO_ZGODY, "odl:" + doKiedy, DNI_ODLOZENIA * 24 * 3600);
        setWidoczny(false);
    }, []);

    if (!widoczny) return null;

    return (
        /* `region`, NIE `dialog`: baner nie blokuje ekranu i nie jest oknem modalnym — jako „dialog” dublowałby
           się z prawdziwymi oknami aplikacji (czytniki ekranu i zapytania o JEDYNE okno).
           `data-baner-cookies` [2026-10-04]: okno „Wygląd aplikacji” czeka, dopóki kafelek czeka na decyzję
           (kafelek leży nad przyciemnieniem okien i poza ich pułapką fokusu — OknoWygladu.SELEKTOR_BANERU_COOKIES). */
        <div className={s.kafelek} role="region" aria-label="Informacja o plikach cookies" data-testid="baner-cookies" data-baner-cookies="">
            <button
                type="button"
                className={s.zamknij}
                onClick={odloz}
                aria-label="Zamknij informację o cookies (bez zgody, przypomnimy za 30 dni)"
                data-testid="baner-cookies-zamknij"
            >
                ✖
            </button>
            <IkonaCiastka />
            <p className={s.tekst}>
                Używamy plików cookies, aby aplikacja działała poprawnie, pamiętała Twoje ustawienia
                i była szybsza. Szczegóły w{" "}
                <Link to={localizedPath("dokumentCookies")} className={s.odsylacz}>polityce cookies</Link>.
            </p>
            <button type="button" className={s.zgoda} onClick={zaakceptuj} data-testid="baner-cookies-zgoda">
                W porządku!
            </button>
        </div>
    );
};

export default BanerCookies;
